import { FC, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import {
  Feature,
  MailSequenceReferenceDateType,
  useMailSequenceSaveTemplateMutation,
  useMailSequenceTemplatesQuery,
} from '../../../../generated/graphql';

import { useFeatureFlags } from '../../../../contexts/FeatureFlagContext';

import Alert from '../../../components/Alert';
import Button from '../../../components/Button';
import InputGroup from '../../../components/InputGroup';
import InputLabel from '../../../components/InputLabel';
import MailAlertMessage, {
  MailAlertItem,
  parseMailPrefixedError,
  textSegment,
} from '../../../components/MailAlertMessage';
import { customToast } from '../../../components/ToastAlert/customToast';
import { FollowUpForm, SavedActivityInputPreview } from '../../../lib/followUp';
import {
  REFERENCE_DATE_OPTIONS,
  StepActivitiesState,
  StepResourceModeState,
  TemplateStepDraft,
  activitiesFingerprint,
  blankStep,
  buildEnrollStepActivitiesFromState,
  buildStepValidationErrors,
  referenceDateLabel,
  templateToDraft,
} from '../../../lib/mailSequence';
import { useSequenceStepsState } from '../../../hooks/useSequenceStepsState';
import usePaintScreen from '../../../hooks/usePaintScreen';
import Spinner from '../../../svgs/Spinner';
import SequenceStepEditor from '../../ScheduleMail/SequenceStepEditor';
import StepResourcePanel from '../../ScheduleMail/StepResourcePanel';
import { formatMailScheduleSummary } from '../../ScheduleMail/anchorDates';
import {
  snapshotActivitiesToSavedPreviews,
  toEnrollmentActivityInputs,
} from '../../ScheduleMail/helpers';
import { scheduleDateTypeLabel } from '../../ScheduleMail/mailTimingValidation';
import TemplateEditorShell from '../Templates/TemplateEditorShell';
import { useUnsavedChangesGuard } from '../Templates/useUnsavedChangesGuard';

const LIST_PATH = '/automations/templates/mail-sequence';

const TEMPLATE_REFERENCE_DATE_OPTIONS = REFERENCE_DATE_OPTIONS.filter(
  (opt) => opt.value !== MailSequenceReferenceDateType.Custom,
);

const draftFingerprint = (
  name: string,
  steps: TemplateStepDraft[],
  referenceDateType: MailSequenceReferenceDateType | null,
  defaultActivities: SavedActivityInputPreview[],
  stepActivitiesState: StepActivitiesState,
  stepResourceModes: StepResourceModeState,
): string =>
  JSON.stringify({
    name: name.trim(),
    referenceDateType,
    steps: steps.map((s) => ({
      offsetDays: s.offsetDays,
      subject: s.subject.trim(),
      providerNote: s.providerNote,
    })),
    defaultActivities: activitiesFingerprint(defaultActivities),
    stepActivities: Object.fromEntries(
      Object.entries(stepActivitiesState).map(([key, value]) => [
        key,
        activitiesFingerprint(value),
      ]),
    ),
    stepModes: stepResourceModes,
  });

const MailSequenceTemplateEditor: FC = () => {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId?: string }>();
  const isCreate = !templateId;
  const paintScreen = usePaintScreen();

  const [name, setName] = useState('');
  const [referenceDateType, setReferenceDateType] =
    useState<MailSequenceReferenceDateType | null>(null);
  const [baselineFp, setBaselineFp] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitErrors, setSubmitErrors] = useState<MailAlertItem[]>([]);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const [hydrated, setHydrated] = useState(isCreate);

  const clearServerError = () => {
    if (serverError) setServerError(null);
  };

  const {
    steps,
    setSteps,
    stepsRevision,
    bumpStepsRevision,
    stepActivitiesState,
    setStepActivitiesState,
    stepResourceModes,
    setStepResourceModes,
    setStepSourceTemplateIds,
    stepSourceTemplateIds,
    setStepActivities,
    setStepResourceMode,
    updateStep,
    addStep,
    removeStep,
  } = useSequenceStepsState({
    minSteps: 1,
    remountStepsOnStructureChange: true,
    onMutate: clearServerError,
  });

  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FollowUpForm>({
    defaultValues: {
      subject: '',
      note: '',
      providerVoiceNoteId: null,
      activities: [],
      patientsOrTags: [],
    },
  });

  const { remove, prepend, update } = useFieldArray({
    control,
    name: 'activities',
  });

  const defaultActivities = (watch('activities') ??
    []) as FollowUpForm['activities'];

  const { data, loading: loadingTemplates } = useMailSequenceTemplatesQuery({
    skip: isCreate,
    fetchPolicy: 'network-only',
    onError: () => {
      customToast.error('Unable to load sequence templates.');
      navigate(LIST_PATH, { replace: true });
    },
  });

  useEffect(() => {
    if (isCreate) {
      const blankSteps = [blankStep(0, 30)];
      setName('');
      setSteps(blankSteps);
      setReferenceDateType(null);
      setValue('activities', []);
      setStepActivitiesState({});
      setStepResourceModes({});
      setStepSourceTemplateIds({});
      setBaselineFp(draftFingerprint('', blankSteps, null, [], {}, {}));
      setHydrated(true);
      return;
    }
    if (!data?.mailSequenceTemplates || !templateId) return;
    const template = data.mailSequenceTemplates.find(
      (t) => t.id === templateId,
    );
    if (!template) {
      customToast.error('Sequence template not found.');
      navigate(LIST_PATH, { replace: true });
      return;
    }

    const draft = templateToDraft(template);
    const nextReferenceDateType =
      template.referenceDateType ===
        MailSequenceReferenceDateType.ProgramStartDate ||
      template.referenceDateType ===
        MailSequenceReferenceDateType.ProgramEndDate
        ? template.referenceDateType
        : null;
    const nextDefaultActivities =
      template.hasDefaultActivities && template.defaultActivityInputs.length
        ? snapshotActivitiesToSavedPreviews(
            template.defaultActivityInputs,
            template.defaultActivities,
          )
        : [];

    const newStepState: StepActivitiesState = {};
    const newStepModes: StepResourceModeState = {};
    const newTemplateIds: Record<number, string> = {};
    [...template.steps]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .forEach((step, i) => {
        if (step.hasActivityOverride) {
          newStepState[i] = step.activityInputs.length
            ? snapshotActivitiesToSavedPreviews(
                step.activityInputs,
                step.activities,
              )
            : [];
          newStepModes[i] = 'custom';
        }
        if (step.sourceFollowUpTemplateId) {
          newTemplateIds[i] = step.sourceFollowUpTemplateId;
        }
      });

    setName(draft.name);
    setSteps(draft.steps);
    setReferenceDateType(nextReferenceDateType);
    setValue('activities', nextDefaultActivities);
    setStepActivitiesState(newStepState);
    setStepResourceModes(newStepModes);
    setStepSourceTemplateIds(newTemplateIds);
    setBaselineFp(
      draftFingerprint(
        draft.name,
        draft.steps,
        nextReferenceDateType,
        nextDefaultActivities,
        newStepState,
        newStepModes,
      ),
    );
    setHydrated(true);
    bumpStepsRevision();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.mailSequenceTemplates, isCreate, navigate, setValue, templateId]);

  const [saveMutation, { loading: saving }] =
    useMailSequenceSaveTemplateMutation({
      onCompleted: ({ mailSequenceSaveTemplate }) => {
        if (mailSequenceSaveTemplate?.success) {
          customToast.success(
            isCreate ? 'Template created.' : 'Template updated.',
          );
          setBaselineFp(null);
          closeUnsavedChangesModal();
          navigate(LIST_PATH);
        } else {
          setServerError(
            mailSequenceSaveTemplate?.error || 'Could not save template.',
          );
        }
      },
      onError: (err) =>
        setServerError(err.message || 'Could not save template.'),
    });

  const anchorLabel = referenceDateType
    ? scheduleDateTypeLabel(referenceDateType)
    : 'chosen date';

  const currentValidationErrors = useMemo((): MailAlertItem[] => {
    const errs: MailAlertItem[] = [];
    if (!name.trim()) {
      errs.push({
        key: 'name',
        segments: [textSegment('Template name is required.')],
      });
    }
    errs.push(...buildStepValidationErrors(steps, anchorLabel));
    return errs;
  }, [anchorLabel, name, steps]);

  const hasUnsavedChanges = useMemo(() => {
    if (!baselineFp) return false;
    return (
      draftFingerprint(
        name,
        steps,
        referenceDateType,
        defaultActivities,
        stepActivitiesState,
        stepResourceModes,
      ) !== baselineFp
    );
  }, [
    baselineFp,
    defaultActivities,
    name,
    referenceDateType,
    stepActivitiesState,
    stepResourceModes,
    steps,
  ]);

  const {
    isUnsavedChangesModalOpen,
    closeUnsavedChangesModal,
    goBack,
    confirmDiscard,
  } = useUnsavedChangesGuard({
    hasUnsavedChanges,
    blockNavigation: Boolean(hasUnsavedChanges && !saving),
    exitPath: LIST_PATH,
    onDiscard: () => setBaselineFp(null),
  });

  useEffect(() => {
    if (!hasAttemptedSave) return;
    setSubmitErrors(currentValidationErrors);
  }, [currentValidationErrors, hasAttemptedSave]);

  const handleSave = () => {
    setHasAttemptedSave(true);
    setSubmitErrors(currentValidationErrors);
    if (currentValidationErrors.length) return;
    setServerError(null);

    const defaultActivityInputs = toEnrollmentActivityInputs(defaultActivities);

    saveMutation({
      variables: {
        input: {
          templateId: templateId ?? null,
          name: name.trim(),
          referenceDateType: referenceDateType ?? null,
          defaultActivities: defaultActivityInputs.length
            ? defaultActivityInputs
            : undefined,
          steps: steps.map((s, i) => {
            const stepActivities = buildEnrollStepActivitiesFromState(
              stepActivitiesState,
              stepResourceModes,
              i,
              defaultActivities,
              toEnrollmentActivityInputs,
            );
            return {
              offsetDays: s.offsetDays,
              subject: s.subject.trim(),
              providerNote: s.providerNote,
              ...(stepActivities ? { activities: stepActivities } : {}),
              ...(stepSourceTemplateIds[i]
                ? { sourceFollowUpTemplateId: stepSourceTemplateIds[i] }
                : {}),
            };
          }),
        },
      },
    });
  };

  const showPage = paintScreen && hydrated && (isCreate || !loadingTemplates);
  const title =
    name.trim() ||
    (isCreate ? 'New Mail sequence template' : 'Mail sequence template');
  const selectedReferenceHelper = referenceDateType
    ? TEMPLATE_REFERENCE_DATE_OPTIONS.find(
        (opt) => opt.value === referenceDateType,
      )?.helper
    : 'Leave unset to choose the date when applying the template and enrolling clients.';

  return (
    <TemplateEditorShell
      show={showPage}
      title={title}
      onBack={goBack}
      headerActions={
        <>
          {(submitErrors.length > 0 || Boolean(serverError)) && (
            <span className="text-small-caption font-medium text-red-125">
              There are errors with this template
            </span>
          )}
          <Button
            title="Add Mail +"
            theme="primary"
            size="small"
            className="px-4 py-1.5"
            onClick={addStep}
            disabled={saving}
          />
          {saving && <Spinner className="h-5 w-5 text-neutral-125" />}
          <Button
            title={saving ? 'Saving...' : 'Save'}
            size="small"
            className="px-6 py-1.5"
            onClick={handleSave}
            disabled={saving}
          />
        </>
      }
      isUnsavedChangesModalOpen={isUnsavedChangesModalOpen}
      onCloseUnsavedChangesModal={closeUnsavedChangesModal}
      onConfirmDiscard={confirmDiscard}
    >
      <div className="w-full max-w-3xl space-y-6 px-6">
        {submitErrors.length > 0 ? (
          <Alert level="error" message="There are issues with this template:">
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {submitErrors.map((item) => (
                <li key={item.key}>
                  <MailAlertMessage segments={item.segments} />
                </li>
              ))}
            </ul>
          </Alert>
        ) : null}
        {serverError ? (
          <Alert level="error" message="Could not save template">
            <p className="mt-1">
              <MailAlertMessage
                segments={parseMailPrefixedError(serverError)}
              />
            </p>
          </Alert>
        ) : null}

        <p className="text-caption text-neutral-110">
          Build a reusable sequence of Mail. Schedule relative to a program date
          set here, or choose the date when enrolling clients.
        </p>

        <InputGroup
          label="Template name"
          placeholder="e.g. Post-retreat 1-3-6 month check-in"
          useNaturalLettering
          value={name}
          onChange={(e) => {
            clearServerError();
            setName(e.target.value);
          }}
        />

        <div>
          <InputLabel
            label="Send Mail relative to this date"
            useNaturalLettering
            containerClassName="mb-1"
          />
          <select
            className="text-sm w-full rounded-md border border-neutral-100 bg-white px-3 py-2 text-body text-neutral-150 focus:border-secondary-100 focus:outline-none focus:ring-2 focus:ring-secondary-50"
            value={referenceDateType ?? ''}
            onChange={(e) => {
              clearServerError();
              const value = e.target.value;
              setReferenceDateType(
                value ? (value as MailSequenceReferenceDateType) : null,
              );
            }}
          >
            <option value="">Choose when enrolling</option>
            {TEMPLATE_REFERENCE_DATE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {selectedReferenceHelper ? (
            <p className="mt-2 text-small-caption text-neutral-110">
              {selectedReferenceHelper}
            </p>
          ) : null}
        </div>

        <div className="space-y-4">
          {steps.map((step, i) => (
            <SequenceStepEditor
              key={`${stepsRevision}-${step.id ?? 'new'}-${i}`}
              stepIndex={i}
              step={step}
              scheduleSummary={formatMailScheduleSummary(
                step.offsetDays,
                referenceDateType
                  ? referenceDateLabel(referenceDateType).toLowerCase()
                  : 'chosen date',
                null,
              )}
              editorKey={`sequence-template-editor-${stepsRevision}-${
                step.id ?? 'new'
              }-${i}`}
              onChange={(patch) => updateStep(i, patch)}
              onRemove={() => removeStep(i)}
              showRemove={steps.length > 1}
              footer={
                <StepResourcePanel
                  stepIndex={i}
                  defaultActivities={defaultActivities}
                  stepActivitiesState={stepActivitiesState}
                  stepResourceModes={stepResourceModes}
                  onSetStepActivities={(stepActivities) =>
                    setStepActivities(i, stepActivities)
                  }
                  onSetStepResourceMode={(mode) => setStepResourceMode(i, mode)}
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  errors={errors}
                  remove={remove}
                  update={update}
                  prepend={prepend}
                />
              }
            />
          ))}
        </div>

        <div className="flex justify-center">
          <Button
            title="Add Mail +"
            theme="primary"
            size="small"
            onClick={addStep}
          />
        </div>
      </div>
    </TemplateEditorShell>
  );
};

const MAIL_TEMPLATES_PATH = '/automations/templates/mail';

const MailSequenceTemplateEditorGate: FC = () => {
  const {
    enabledFeatures,
    loading: featuresLoading,
    error: featuresError,
    isFeatureEnabled,
  } = useFeatureFlags();
  const featuresLoaded = enabledFeatures !== undefined;

  if (featuresLoading && !featuresLoaded) {
    return null;
  }
  // Failed flag fetch or missing access → leave rather than a blank page.
  if (featuresError || !isFeatureEnabled(Feature.MailSequences)) {
    return <Navigate to={MAIL_TEMPLATES_PATH} replace />;
  }
  return <MailSequenceTemplateEditor />;
};

export default MailSequenceTemplateEditorGate;
